import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import '../style/css/nav2026.css';
import logo from '../style/imges/hopalogo.png';
import Icon from '../comps/Icon';
import { isPlanPreviewAllowed } from '../services/plan';
import managedata from '../services/managedata';
import managecookies from '../services/managecookies';

// Icons are names into the shared <Icon> set (no emojis, no per-file svg imports).
// LIVE is the single day-management page, visible on EVERY plan: the basic plan renders
// it as a by-hour list of today's reservations, the pro plan as the visual LIVE board
// (the page itself adapts by plan — see MainPage /board route).
const LINKS = [
    { to: '/', label: 'דף בית', icon: 'home', end: true },
    { to: '/board', label: 'LIVE', icon: 'board' },
    { to: '/waitlist', label: 'רשימת המתנה', icon: 'list' },
    { to: '/calendar', label: 'יומן', icon: 'calendar' },
    { to: '/live', label: 'שולחנות', icon: 'tables' },
    { to: '/clients', label: 'לקוחות', icon: 'customer' },
    { to: '/business', label: 'ניהול העסק', icon: 'store' },
    { to: '/share', label: 'שיתוף הזמנות', icon: 'share' },
    { to: '/sms', label: 'הודעות SMS', icon: 'mail' },
    { to: '/stats', label: 'סטטיסטיקות', icon: 'chart' },
    // מנויים — the tier-compare/preview page. Shown only to the operator's own businesses
    // (isPlanPreviewAllowed), so it isn't exposed to regular paying customers.
    { to: '/plans', label: 'מנויים', icon: 'coin', previewOnly: true },
    { to: '/settings', label: 'הגדרות', icon: 'settings' },
];

const COLLAPSE_KEY = 'nav-collapsed';

const NavBar = () => {
    const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth <= 768);
    const [open, setOpen] = useState(typeof window === 'undefined' || window.innerWidth > 768);
    // Desktop-only: collapse to an icons-only rail for more screen space (persisted).
    const [collapsed, setCollapsed] = useState(() => {
        try { return typeof window !== 'undefined' && window.localStorage.getItem(COLLAPSE_KEY) === '1'; }
        catch (e) { return false; }
    });

    useEffect(() => {
        const onResize = () => {
            const mobile = window.innerWidth <= 768;
            setIsMobile(mobile);
            if (!mobile) setOpen(true);
        };
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    const toggleCollapsed = () => {
        setCollapsed((c) => {
            const next = !c;
            try { localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0'); } catch (e) { /* ignore */ }
            return next;
        });
    };

    const closeOnMobile = () => { if (isMobile) setOpen(false); };
    const isCollapsed = collapsed && !isMobile;

    // Logout — tell the server to revoke the session, then drop the local cookies and
    // navigate to the login gate so no stale in-memory app state survives. We AWAIT the
    // server call (so token revocation actually lands before we navigate away and cancel
    // the request), but race it against a short timeout so a slow/failed call can never
    // trap the user in the app — either way we clear locally and leave.
    const handleLogout = async () => {
        closeOnMobile();
        const serverLogout = new Promise((resolve) => {
            managedata.sendData({ data: {}, type: { act: 'logout' } }, resolve, resolve);
        });
        const timeout = new Promise((resolve) => setTimeout(resolve, 1500));
        await Promise.race([serverLogout, timeout]);
        managecookies.removeAllCookies();
        try { localStorage.removeItem('area-list'); } catch (e) { /* ignore */ }
        window.location.assign('/login');
    };

    // Every nav item shows on every plan; the LIVE page adapts its own view by plan. The
    // מנויים (tier preview) item is shown only to the operator's allowlisted businesses.
    const planPreviewOk = isPlanPreviewAllowed();
    const links = LINKS.filter((l) => !l.previewOnly || planPreviewOk);

    return (
        <>
            {isMobile && open && <div className="nav-overlay" onClick={() => setOpen(false)} />}
            <nav className={`nav ${open ? 'nav--open' : ''} ${isCollapsed ? 'nav--collapsed' : ''}`}>
                <div className="nav-top">
                    <img className="nav-logo" src={logo} alt="HOPpA" />
                    {!isMobile && (
                        <button className="nav-collapse-btn" onClick={toggleCollapsed}
                            aria-label={isCollapsed ? 'הרחבת תפריט' : 'צמצום תפריט'} title={isCollapsed ? 'הרחבה' : 'צמצום'}>
                            <Icon name={isCollapsed ? 'chevron-left' : 'panel-collapse'} size={20} />
                        </button>
                    )}
                </div>
                <ul className="nav-list">
                    {links.map((l) => (
                        <li key={l.to}>
                            <NavLink to={l.to} end={l.end} onClick={closeOnMobile}
                                title={isCollapsed ? l.label : undefined}
                                className={({ isActive }) => `nav-link ${isActive ? 'nav-link--active' : ''}`}>
                                <Icon className="nav-icon" name={l.icon} size={21} />
                                <span className="nav-label">{l.label}</span>
                            </NavLink>
                        </li>
                    ))}
                </ul>
                <button type="button" className="nav-logout" onClick={handleLogout}
                    title={isCollapsed ? 'יציאה' : undefined}>
                    <Icon className="nav-icon" name="logout" size={21} />
                    <span className="nav-label">יציאה</span>
                </button>
            </nav>
            {isMobile && (
                <button className="nav-hamburger" onClick={() => setOpen(!open)} aria-label="תפריט">
                    <Icon name="menu" size={24} />
                </button>
            )}
        </>
    );
};

export default NavBar;
